import React, { useState, useEffect } from "react";
import {
  Box,
  Select,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Text,
  useToast,
  Spinner,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { supabase } from "../../../lib/supabaseClient";

const PLATFORMS = [
  { key: "melon", label: "멜론" },
  { key: "genie", label: "지니" },
  { key: "vibe", label: "바이브" },
  { key: "flo", label: "플로" },
  { key: "global", label: "해외" },
  { key: "youtube", label: "유튜브" },
];

const FeeManagement = () => {
  const [selectedMonth, setSelectedMonth] = useState("");
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState({});
  const [buyersAmountTotalSum, setBuyersAmountTotalSum] = useState(null);
  const toast = useToast();
  const [settlementSwitch, setSettlementSwitch] = useState(null);
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tempPage, setTempPage] = useState("1");
  const [applyingFee, setApplyingFee] = useState(false);
  const [owners, setOwners] = useState([]);
  const [loadingOwners, setLoadingOwners] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const {
    isOpen: isNotificationModalOpen,
    onOpen: onNotificationModalOpen,
    onClose: onNotificationModalClose,
  } = useDisclosure();
  const [sendingSMS, setSendingSMS] = useState(false);
  const [smsFailures, setSmsFailures] = useState([]);
  const [isYoutubeNotificationModalOpen, setIsYoutubeNotificationModalOpen] =
    useState(false);
  const [youtubeOwners, setYoutubeOwners] = useState([]);
  const [loadingYoutubeOwners, setLoadingYoutubeOwners] = useState(false);
  const [sendingYoutubeSMS, setSendingYoutubeSMS] = useState(false);
  const [youtubeSmsFailures, setYoutubeSmsFailures] = useState([]);
  const [youtubeSearchTerm, setYoutubeSearchTerm] = useState("");
  const [youtubeCurrentPage, setYoutubeCurrentPage] = useState(1);

  // 24개월 정산월 목록 생성 (202505 형식)
  useEffect(() => {
    const generateMonths = () => {
      const result = [];
      const currentDate = new Date();
      for (let i = 0; i < 36; i++) {
        const date = new Date(currentDate);
        date.setMonth(currentDate.getMonth() - i);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        result.push(`${year}${month}`);
      }
      setMonths(result);
      setSelectedMonth(result[0]);
    };
    generateMonths();
  }, []);

  // 정산 데이터 fetch (예시, 실제 테이블/컬럼명에 맞게 수정 필요)
  const fetchData = async (monthParam) => {
    setLoading(true);
    const monthToSend = monthParam ?? selectedMonth;
    try {
      const { data, error } = await supabase.rpc("fetch_settlement_summary", {
        month_param: monthToSend,
      });
      if (error) {
        console.error(error);
        setTableData({});
      } else {
        setTableData(data || {});
      }
    } catch (err) {
      console.error(err);
      setTableData({});
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!selectedMonth) return;
    fetchData(selectedMonth);
  }, [selectedMonth]);

  // payout_buyer의 amount_total 합계 fetch (정산 완료 금액)
  useEffect(() => {
    if (!selectedMonth) return;
    const fetchBuyersAmountTotal = async () => {
      const { data, error } = await supabase
        .from("payout_buyer")
        .select("amount_total")
        .eq("settlement_month", selectedMonth);
      if (error || !Array.isArray(data)) {
        setBuyersAmountTotalSum(null);
        return;
      }
      const sum = data.reduce(
        (acc, row) => acc + (Number(row.amount_total) || 0),
        0
      );
      setBuyersAmountTotalSum(sum);
    };
    fetchBuyersAmountTotal();
  }, [selectedMonth]);

  // settings 테이블에서 settlement switch 값 불러오기
  useEffect(() => {
    const fetchSettlementSwitch = async () => {
      const { data, error } = await supabase
        .from("settings")
        .select("setting_switch")
        .eq("setting_name", "settlement")
        .maybeSingle();
      if (!error && data) {
        setSettlementSwitch(data.setting_switch);
      }
    };
    fetchSettlementSwitch();
  }, []);

  // 정산 중단/시작 토글 핸들러
  const handleToggleSettlement = async () => {
    if (settlementSwitch === null) return;
    setSettlementLoading(true);
    try {
      const { error } = await supabase
        .from("settings")
        .update({ setting_switch: !settlementSwitch })
        .eq("setting_name", "settlement");
      if (error) throw error;
      setSettlementSwitch((prev) => !prev);
      toast({
        title: `정산 ${settlementSwitch ? "시작" : "중단"} 처리 완료`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (e) {
      toast({
        title: "정산 상태 변경 실패",
        description: e.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSettlementLoading(false);
    }
  };

  const handleSendNotification = async () => {
    setLoadingOwners(true);
    try {
      const { data, error } = await supabase.rpc("get_owners_by_month", {
        target_month: selectedMonth,
      });

      if (error) throw error;
      setOwners(data || []);
      onNotificationModalOpen();
    } catch (error) {
      toast({
        title: "소유자 정보 조회 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingOwners(false);
    }
  };

  const handleApplyFee = async (platformLabel, platformKey) => {
    const month = selectedMonth;
    setApplyingFee(true);
    try {
      const { error, data } = await supabase.rpc(
        "calculate_settlement_commissions",
        {
          platform: platformKey,
          target_month: month,
        }
      );
      if (error) throw error;

      if (platformKey === "youtube" || platformKey === "global") {
        const { error2, data2 } = await supabase.rpc(
          "calculate_settlement_commissions2",
          {
            platform: platformKey,
            target_month: month,
          }
        );
        if (error2) throw error2;
      }

      toast({
        title: `${platformLabel} 수수료 적용 완료`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      // 데이터 새로고침
      await fetchData(month);
    } catch (err) {
      toast({
        title: `${platformLabel} 수수료 적용 실패`,
        description: err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setApplyingFee(false);
    }
  };

  useEffect(() => {
    setTempPage(page.toString());
  }, [page]);
  const handlePageInput = (e) => setTempPage(e.target.value);
  const handlePageMove = () => {
    if (/^\d+$/.test(tempPage)) {
      const p = parseInt(tempPage);
      if (p >= 1 && p <= totalPages) setPage(p);
      else setTempPage(page.toString());
    } else setTempPage(page.toString());
  };

  // 검색어에 따른 필터링된 데이터
  const filteredOwners = owners.filter(
    (owner) =>
      owner.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      owner.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      owner.phone_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // 페이지네이션을 위한 데이터 계산
  const ownersTotalPages = Math.ceil(filteredOwners.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOwners = filteredOwners.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // 검색 시 첫 페이지로 이동
  };

  const handleSendSettlementSMS = async () => {
    setSendingSMS(true);
    setSmsFailures([]);

    try {
      // 전화번호 목록 준비 (0을 앞에 붙이고 '-' 제외)
      let phones = filteredOwners
        .filter((owner) => owner.phone_number && owner.phone_number !== "-")
        .map((owner) => {
          const phone = owner.phone_number.replace(/-/g, "");
          return phone.startsWith("0") ? phone : `0${phone}`;
        });

      if (phones.length === 0) {
        toast({
          title: "알림 발송 실패",
          description: "발송할 전화번호가 없습니다.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const response = await fetch(
        "https://hupnidxelbhaluxrsygd.supabase.co/functions/v1/send-sms-settlement",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ phones }),
        }
      );

      const result = await response.json();

      if (result.failures && result.failures.length > 0) {
        setSmsFailures(result.failures);
        toast({
          title: "일부 알림 발송 실패",
          description: `${result.failures.length}개의 알림 발송에 실패했습니다.`,
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "알림 발송 완료",
          description: "모든 알림이 성공적으로 발송되었습니다.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "알림 발송 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSendingSMS(false);
    }
  };

  const handleSendYoutubeNotification = async () => {
    setLoadingYoutubeOwners(true);
    try {
      const { data, error } = await supabase.rpc(
        "get_youtube_owners_by_month",
        {
          target_month: selectedMonth,
        }
      );

      if (error) throw error;
      setYoutubeOwners(data || []);
      setIsYoutubeNotificationModalOpen(true);
    } catch (error) {
      toast({
        title: "유튜브 소유자 정보 조회 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingYoutubeOwners(false);
    }
  };

  // 유튜브 검색어에 따른 필터링된 데이터
  const filteredYoutubeOwners = youtubeOwners.filter(
    (owner) =>
      owner.name?.toLowerCase().includes(youtubeSearchTerm.toLowerCase()) ||
      owner.email?.toLowerCase().includes(youtubeSearchTerm.toLowerCase()) ||
      owner.phone_number
        ?.toLowerCase()
        .includes(youtubeSearchTerm.toLowerCase())
  );

  // 유튜브 페이지네이션을 위한 데이터 계산
  const youtubeOwnersTotalPages = Math.ceil(
    filteredYoutubeOwners.length / itemsPerPage
  );
  const youtubeStartIndex = (youtubeCurrentPage - 1) * itemsPerPage;
  const paginatedYoutubeOwners = filteredYoutubeOwners.slice(
    youtubeStartIndex,
    youtubeStartIndex + itemsPerPage
  );

  const handleYoutubePageChange = (newPage) => {
    setYoutubeCurrentPage(newPage);
  };

  const handleYoutubeSearch = (e) => {
    setYoutubeSearchTerm(e.target.value);
    setYoutubeCurrentPage(1);
  };

  const handleSendYoutubeSettlementSMS = async () => {
    setSendingYoutubeSMS(true);
    setYoutubeSmsFailures([]);

    try {
      let phones = filteredYoutubeOwners
        .filter((owner) => owner.phone_number && owner.phone_number !== "-")
        .map((owner) => {
          const phone = owner.phone_number.replace(/-/g, "");
          return phone.startsWith("0") ? phone : `0${phone}`;
        });

      if (phones.length === 0) {
        toast({
          title: "알림 발송 실패",
          description: "발송할 전화번호가 없습니다.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const response = await fetch(
        "https://hupnidxelbhaluxrsygd.supabase.co/functions/v1/send-sms-settlement",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ phones }),
        }
      );

      const result = await response.json();

      if (result.failures && result.failures.length > 0) {
        setYoutubeSmsFailures(result.failures);
        toast({
          title: "일부 알림 발송 실패",
          description: `${result.failures.length}개의 알림 발송에 실패했습니다.`,
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "알림 발송 완료",
          description: "모든 알림이 성공적으로 발송되었습니다.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "알림 발송 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSendingYoutubeSMS(false);
    }
  };

  return (
    <Box p={4}>
      {/* 상단 컨트롤 섹션 */}
      <HStack mb={6} justify="space-between">
        <Select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          width="200px"
        >
          {months.map((month) => (
            <option key={month} value={month}>
              {month}
            </option>
          ))}
        </Select>
        <HStack spacing={4}>
          {settlementSwitch !== null && (
            <Button
              colorScheme={settlementSwitch ? "red" : "green"}
              onClick={handleToggleSettlement}
              isLoading={settlementLoading}
            >
              {settlementSwitch ? "정산 중단" : "정산 시작"}
            </Button>
          )}
          <Button
            colorScheme="blue"
            onClick={handleSendNotification}
            isLoading={loadingOwners}
            loadingText="조회 중..."
          >
            알림 발송
          </Button>
          <Button
            colorScheme="purple"
            onClick={handleSendYoutubeNotification}
            isLoading={loadingYoutubeOwners}
            loadingText="조회 중..."
          >
            유튜브 알림 발송
          </Button>
        </HStack>
      </HStack>

      {/* 수수료 테이블 */}
      <Box overflowX="auto" mb={6}>
        {loading ? (
          <Box py={10} textAlign="center">
            <Spinner size="lg" />
          </Box>
        ) : (
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th></Th>
                {PLATFORMS.map((p) => (
                  <Th key={p.key} textAlign="center">
                    {p.label}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {/* 정산 금액 */}
              <Tr>
                <Td fontWeight="bold">정산 금액</Td>
                {PLATFORMS.map((p) => (
                  <Td key={p.key + "-amount"} textAlign="center">
                    {typeof tableData[p.key]?.amountSum === "number"
                      ? tableData[p.key].amountSum.toLocaleString() + "원"
                      : "0원"}
                  </Td>
                ))}
              </Tr>
              {/* 수수료 */}
              <Tr>
                <Td fontWeight="bold">수수료</Td>
                {PLATFORMS.map((p) => {
                  // youtube는 정산 금액 - 최종 정산 금액
                  let commission = 0;
                  if (p.key === "youtube") {
                    const amount =
                      typeof tableData[p.key]?.amountSum === "number"
                        ? tableData[p.key].amountSum
                        : 0;
                    const finalSum =
                      typeof tableData[p.key]?.finalSum === "number"
                        ? tableData[p.key].finalSum
                        : 0;
                    commission = amount - finalSum;
                  } else {
                    commission =
                      typeof tableData[p.key]?.commissionSum === "number"
                        ? tableData[p.key].commissionSum
                        : 0;
                  }
                  return (
                    <Td key={p.key + "-commission"} textAlign="center">
                      {commission.toLocaleString()}원
                    </Td>
                  );
                })}
              </Tr>
              {/* 정산 금액 - 수수료 */}
              <Tr>
                <Td fontWeight="bold">정산 금액 - 수수료</Td>
                {PLATFORMS.map((p) => {
                  // youtube는 최종 정산 금액
                  let afterCommission = 0;
                  if (p.key === "youtube") {
                    afterCommission =
                      typeof tableData[p.key]?.finalSum === "number"
                        ? tableData[p.key].finalSum
                        : 0;
                  } else {
                    afterCommission =
                      typeof tableData[p.key]?.afterCommission === "number"
                        ? tableData[p.key].afterCommission
                        : 0;
                  }
                  return (
                    <Td key={p.key + "-afterCommission"} textAlign="center">
                      {afterCommission.toLocaleString()}원
                    </Td>
                  );
                })}
              </Tr>
              {/* 수수료율 */}
              <Tr>
                <Td fontWeight="bold">수수료율</Td>
                {PLATFORMS.map((p) => {
                  let rate = tableData[p.key]?.commissionRate;
                  if (p.key === "youtube") {
                    // 수수료율 = (수수료 / 정산금액) * 100
                    const amount =
                      typeof tableData[p.key]?.amountSum === "number"
                        ? tableData[p.key].amountSum
                        : 0;
                    const finalSum =
                      typeof tableData[p.key]?.finalSum === "number"
                        ? tableData[p.key].finalSum
                        : 0;
                    const commission = amount - finalSum;
                    if (amount > 0) {
                      rate = ((commission / amount) * 100).toFixed(2) + "%";
                    } else {
                      rate = "0%";
                    }
                  } else {
                    if (!rate || rate === "-") rate = "0%";
                    else if (!rate.endsWith("%")) rate = rate + "%";
                  }
                  return (
                    <Td key={p.key + "-commissionRate"} textAlign="center">
                      {rate}
                    </Td>
                  );
                })}
              </Tr>
              {/* 최종 정산 금액 */}
              <Tr>
                <Td fontWeight="bold">최종 정산 금액</Td>
                {PLATFORMS.map((p) => (
                  <Td key={p.key + "-finalSum"} textAlign="center">
                    {typeof tableData[p.key]?.finalSum === "number"
                      ? tableData[p.key].finalSum.toLocaleString() + "원"
                      : "0원"}
                  </Td>
                ))}
              </Tr>
              {/* 차액 */}
              <Tr>
                <Td fontWeight="bold">차액</Td>
                {PLATFORMS.map((p) => (
                  <Td key={p.key + "-diff"} textAlign="center">
                    {typeof tableData[p.key]?.diff === "number"
                      ? tableData[p.key].diff.toLocaleString() + "원"
                      : "0원"}
                  </Td>
                ))}
              </Tr>
            </Tbody>
          </Table>
        )}
      </Box>

      {/* 수수료 적용 버튼들 */}
      <HStack spacing={4} mb={6} wrap="wrap">
        {PLATFORMS.map((p) => (
          <Button
            key={p.key}
            colorScheme="blue"
            onClick={() => handleApplyFee(p.label, p.key)}
            isLoading={applyingFee}
            loadingText="적용 중..."
            isDisabled={applyingFee}
          >
            {p.label} 수수료 적용
          </Button>
        ))}
      </HStack>

      {/* 최종 정산 금액 섹션 */}
      <Box p={4} bg="blue.50" borderRadius="lg" boxShadow="sm">
        <HStack spacing={8}>
          <Box>
            <Text fontSize="md" color="gray.600">
              총 정산 금액
            </Text>
            <Text fontSize="xl" fontWeight="bold" color="blue.700">
              {(() => {
                const totalFinal = PLATFORMS.reduce((sum, p) => {
                  const v = tableData[p.key]?.finalSum;
                  return sum + (typeof v === "number" ? v : 0);
                }, 0);
                return totalFinal.toLocaleString() + "원";
              })()}
            </Text>
          </Box>
          <Box>
            <Text fontSize="md" color="gray.600">
              정산 완료 금액
            </Text>
            <Text fontSize="xl" fontWeight="bold" color="blue.700">
              {(() => {
                const value = buyersAmountTotalSum ?? 0;
                return value.toLocaleString() + "원";
              })()}
            </Text>
          </Box>
          <Box>
            <Text fontSize="md" color="gray.600">
              잔여 정산 금액
            </Text>
            <Text fontSize="xl" fontWeight="bold" color="blue.700">
              {(() => {
                const totalFinal = PLATFORMS.reduce((sum, p) => {
                  const v = tableData[p.key]?.finalSum;
                  return sum + (typeof v === "number" ? v : 0);
                }, 0);
                const done = buyersAmountTotalSum ?? 0;
                const remain = done - totalFinal;
                const absRemain = Math.abs(remain).toLocaleString();
                if (remain > 0) return `-${absRemain}원`;
                if (remain < 0) return `${absRemain}원`;
                return "0원";
              })()}
            </Text>
          </Box>
        </HStack>
      </Box>

      {/* 페이지네이션 */}
      <HStack mt={6} justify="center" spacing={2}>
        <Button
          size="sm"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          isDisabled={page === 1}
        >
          이전
        </Button>
        <HStack spacing={1} mx={2}>
          <Input
            value={tempPage}
            onChange={handlePageInput}
            size="sm"
            width="40px"
            textAlign="center"
            fontSize="sm"
          />
          <Button
            size="sm"
            onClick={handlePageMove}
            colorScheme="gray"
            variant="outline"
            minW="32px"
            px={2}
            color="gray.600"
            borderColor="gray.300"
            _hover={{ bg: "gray.100", borderColor: "gray.400" }}
          >
            이동
          </Button>
          <Text fontSize="sm">/ {totalPages}</Text>
        </HStack>
        <Button
          size="sm"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          isDisabled={page === totalPages}
        >
          다음
        </Button>
      </HStack>

      {/* 알림 발송 모달 */}
      <Modal
        isOpen={isNotificationModalOpen}
        onClose={onNotificationModalClose}
        size="2xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>정산 대상자 목록</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Box mb={4}>
              <Input
                placeholder="이름, 이메일, 전화번호로 검색"
                value={searchTerm}
                onChange={handleSearch}
                size="md"
              />
            </Box>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>이름</Th>
                  <Th>이메일</Th>
                  <Th>전화번호</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedOwners.map((owner, index) => (
                  <Tr key={index}>
                    <Td>{owner.name || "-"}</Td>
                    <Td>{owner.email}</Td>
                    <Td>{owner.phone_number || "-"}</Td>
                  </Tr>
                ))}
                {paginatedOwners.length === 0 && (
                  <Tr>
                    <Td colSpan={3} textAlign="center">
                      {searchTerm
                        ? "검색 결과가 없습니다."
                        : "정산 대상자가 없습니다."}
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>

            {/* 페이지네이션 */}
            {ownersTotalPages > 1 && (
              <HStack mt={4} justify="center" spacing={2}>
                <Button
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  isDisabled={currentPage === 1}
                >
                  이전
                </Button>
                <Text>
                  {currentPage} / {ownersTotalPages}
                </Text>
                <Button
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  isDisabled={currentPage === ownersTotalPages}
                >
                  다음
                </Button>
              </HStack>
            )}

            {/* 검색 결과 요약 */}
            <Text mt={4} fontSize="sm" color="gray.600" textAlign="center">
              총 {filteredOwners.length}명의 정산 대상자가 있습니다.
              {searchTerm && ` (검색어: ${searchTerm})`}
            </Text>

            {/* 정산 알림 발송 버튼 */}
            <Box mt={4} textAlign="center">
              <Button
                colorScheme="blue"
                onClick={handleSendSettlementSMS}
                isLoading={sendingSMS}
                loadingText="발송 중..."
                isDisabled={sendingSMS || filteredOwners.length === 0}
              >
                정산 알림 발송하기
              </Button>
            </Box>

            {/* 실패한 전송 결과 */}
            {smsFailures.length > 0 && (
              <Box mt={4}>
                <Text fontWeight="bold" color="red.500" mb={2}>
                  발송 실패 목록 ({smsFailures.length}건)
                </Text>
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>전화번호</Th>
                      <Th>에러 메시지</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {smsFailures.map((failure, index) => (
                      <Tr key={index}>
                        <Td>{failure.phone}</Td>
                        <Td>{failure.error}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* 유튜브 알림 발송 모달 */}
      <Modal
        isOpen={isYoutubeNotificationModalOpen}
        onClose={() => setIsYoutubeNotificationModalOpen(false)}
        size="2xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>유튜브 정산 대상자 목록</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Box mb={4}>
              <Input
                placeholder="이름, 이메일, 전화번호로 검색"
                value={youtubeSearchTerm}
                onChange={handleYoutubeSearch}
                size="md"
              />
            </Box>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>이름</Th>
                  <Th>이메일</Th>
                  <Th>전화번호</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedYoutubeOwners.map((owner, index) => (
                  <Tr key={index}>
                    <Td>{owner.name || "-"}</Td>
                    <Td>{owner.email}</Td>
                    <Td>{owner.phone_number || "-"}</Td>
                  </Tr>
                ))}
                {paginatedYoutubeOwners.length === 0 && (
                  <Tr>
                    <Td colSpan={3} textAlign="center">
                      {youtubeSearchTerm
                        ? "검색 결과가 없습니다."
                        : "정산 대상자가 없습니다."}
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>

            {/* 페이지네이션 */}
            {youtubeOwnersTotalPages > 1 && (
              <HStack mt={4} justify="center" spacing={2}>
                <Button
                  size="sm"
                  onClick={() =>
                    handleYoutubePageChange(youtubeCurrentPage - 1)
                  }
                  isDisabled={youtubeCurrentPage === 1}
                >
                  이전
                </Button>
                <Text>
                  {youtubeCurrentPage} / {youtubeOwnersTotalPages}
                </Text>
                <Button
                  size="sm"
                  onClick={() =>
                    handleYoutubePageChange(youtubeCurrentPage + 1)
                  }
                  isDisabled={youtubeCurrentPage === youtubeOwnersTotalPages}
                >
                  다음
                </Button>
              </HStack>
            )}

            {/* 검색 결과 요약 */}
            <Text mt={4} fontSize="sm" color="gray.600" textAlign="center">
              총 {filteredYoutubeOwners.length}명의 정산 대상자가 있습니다.
              {youtubeSearchTerm && ` (검색어: ${youtubeSearchTerm})`}
            </Text>

            {/* 정산 알림 발송 버튼 */}
            <Box mt={4} textAlign="center">
              <Button
                colorScheme="purple"
                onClick={handleSendYoutubeSettlementSMS}
                isLoading={sendingYoutubeSMS}
                loadingText="발송 중..."
                isDisabled={
                  sendingYoutubeSMS || filteredYoutubeOwners.length === 0
                }
              >
                유튜브 정산 알림 발송하기
              </Button>
            </Box>

            {/* 실패한 전송 결과 */}
            {youtubeSmsFailures.length > 0 && (
              <Box mt={4}>
                <Text fontWeight="bold" color="red.500" mb={2}>
                  발송 실패 목록 ({youtubeSmsFailures.length}건)
                </Text>
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>전화번호</Th>
                      <Th>에러 메시지</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {youtubeSmsFailures.map((failure, index) => (
                      <Tr key={index}>
                        <Td>{failure.phone}</Td>
                        <Td>{failure.error}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default FeeManagement;
